import { graphql } from 'gatsby';
import { useMixpanel } from 'gatsby-plugin-mixpanel';
import React, { ReactElement, useEffect } from 'react';
import { Body, Headline, Text } from '../../../components';
import { formatDate } from '../../../utilities';
import Avatar from '../../avatar';
import Block from '../../block';
import Meta from '../../meta';
import GlobalTemplate from '../../templates/global';
import './style.css';

interface IProps {
  data: any;
}

// The layout for an individual blog post
export default function BlogPost({
  data: { strapiBlogPost },
}: IProps): ReactElement {
  const mixpanel = useMixpanel();

  const {
    date,
    slug,
    title,
    subtitle,
    categories,
    author,
    content,
    headerImage,
  } = strapiBlogPost;

  const getTags = () => {
    let tagString = '';
    categories.forEach((tag) => {
      if (tagString === '') {
        tagString += tag.name;
      } else {
        tagString += `, ${tag.name}`;
      }
    });

    return tagString;
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      mixpanel.track('Resource clicked', {
        'content type': 'blog',
        categories: categories.map((category) => category.name),
        title,
      });
    }
  }, [mixpanel]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const scriptTag = document.createElement('script');
      scriptTag.src = '/prism.js';

      document.body.appendChild(scriptTag);
    }
  }, []);

  return (
    <GlobalTemplate navBgColor="bone">
      <Meta
        title={title}
        siteName="Stark Blog"
        ogType="article"
        ogTitle={title}
        ogDescription={subtitle}
        ogUrl={`https://getstark.co/blog/${slug}`}
        ogImgUrl={headerImage?.url}>
        <meta property="article:published_time" content={date} />
        <meta property="article:tag" content={categories[0].name} />
        <meta name="twitter:label1" content="Written by" />
        <meta name="twitter:data1" content={author.name} />
        <meta name="twitter:label2" content="Filed under" />
        <meta name="twitter:data2" content={categories[0].name} />
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Stark Blog"
          href="https://getstark.co/blog-rss.xml"
        />
        <link rel="stylesheet" href="/prism.css" />
        <script
          async
          src="https://platform.twitter.com/widgets.js"
          charSet="utf-8"></script>
      </Meta>

      <main>
        <article>
          <Block width={600}>
            <header>
              {/* <Text level="mono" color="purple" className="mb-2 mt-16">
                {getTags()}
              </Text> */}

              <Headline level="2" className="mb-4 mt-16">
                {title}
              </Headline>

              <Body className="mb-6">{subtitle}</Body>
              <div className="mb-12 flex items-center border-b border-b-neutral-gray-100 pb-10">
                <Avatar
                  name={author.name}
                  srcUrl={author.image.formats.thumbnail.url}
                />

                <div>
                  <Text level="regular" color="blue">
                    {author.name}
                  </Text>

                  <Text level="mono" className="t-color-gray uppercase">
                    {formatDate(date)}
                  </Text>
                </div>
              </div>
            </header>
          </Block>

          <Block>
            <div className="blog-content prose-lg md:prose-xl">
              {headerImage && (
                <img
                  className="content__blog-img mx-auto mb-10 mt-12 max-h-[640px] rounded"
                  alt={headerImage.alternativeText}
                  loading="lazy"
                  src={headerImage.url}
                />
              )}

              <div
                dangerouslySetInnerHTML={{
                  __html: content.data.content,
                }}
              />
            </div>
          </Block>
        </article>
      </main>
    </GlobalTemplate>
  );
}

export const pageQuery = graphql`
  query StrapiPostQuery($id: String) {
    strapiBlogPost(id: { eq: $id }) {
      content {
        data {
          content
        }
      }
      date
      slug
      headerImage {
        alternativeText
        caption
        url
      }
      subtitle
      title
      categories {
        name
      }
      author {
        name
        image {
          formats {
            thumbnail {
              url
            }
          }
        }
      }
    }
  }
`;
